import { useState, useMemo } from 'react';

/**
 * Hook personalizado para manejar filtros y paginación de planillas
 * @param {Array} planillas - Lista de planillas
 * @param {number} itemsPerPage - Número de items por página
 * @returns {Object} - Estado de filtros, paginación y funciones
 */
export const usePlanillasFilters = (planillas, itemsPerPage = 10) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [fechaInicioFiltro, setFechaInicioFiltro] = useState("");
  const [fechaFinFiltro, setFechaFinFiltro] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Resetear página cuando cambian los filtros
  const resetPageOnFilterChange = () => {
    setCurrentPage(1);
  };

  // Planillas filtradas
  const planillasFiltradas = useMemo(() => {
    return planillas.filter((planilla) => {
      const cumpleBusqueda = planilla.periodo
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

      const cumpleEstado =
        filtroEstado === "TODOS" || planilla.estado === filtroEstado;

      const cumpleFechaInicio = !fechaInicioFiltro ||
        (planilla.fecha_inicio &&
         new Date(planilla.fecha_inicio.toDate ? planilla.fecha_inicio.toDate() : planilla.fecha_inicio) >=
         new Date(fechaInicioFiltro));

      const cumpleFechaFin = !fechaFinFiltro ||
        (planilla.fecha_fin &&
         new Date(planilla.fecha_fin.toDate ? planilla.fecha_fin.toDate() : planilla.fecha_fin) <=
         new Date(fechaFinFiltro));

      return cumpleBusqueda && cumpleEstado && cumpleFechaInicio && cumpleFechaFin;
    });
  }, [planillas, searchTerm, filtroEstado, fechaInicioFiltro, fechaFinFiltro]);

  // Paginación
  const totalPages = Math.ceil(planillasFiltradas.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const planillasPaginadas = planillasFiltradas.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setSearchTerm("");
    setFiltroEstado("TODOS");
    setFechaInicioFiltro("");
    setFechaFinFiltro("");
    setCurrentPage(1);
  };

  return {
    // Estados
    searchTerm,
    filtroEstado,
    fechaInicioFiltro,
    fechaFinFiltro,
    currentPage,

    // Setters con reset de página
    setSearchTerm: (value) => {
      setSearchTerm(value);
      resetPageOnFilterChange();
    },
    setFiltroEstado: (value) => {
      setFiltroEstado(value);
      resetPageOnFilterChange();
    },
    setFechaInicioFiltro: (value) => {
      setFechaInicioFiltro(value);
      resetPageOnFilterChange();
    },
    setFechaFinFiltro: (value) => {
      setFechaFinFiltro(value);
      resetPageOnFilterChange();
    },

    // Datos calculados
    planillasFiltradas,
    planillasPaginadas,
    totalPages,

    // Funciones
    handlePageChange,
    limpiarFiltros
  };
};